import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link as MuiLink,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Language as WebsiteIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as ConnectedIcon,
  Warning as WarningIcon,
  PanoramaFishEye as ManualIcon,
} from '@mui/icons-material';
import TermHelp from '../components/TermHelp';
import PageHeader from '../components/PageHeader';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Static ingestion metadata per authority code (supplements DB data for demo)
const AUTHORITY_METADATA: Record<string, {
  ingestionStatus: 'automated' | 'semi-automated' | 'manual-portal' | 'manual';
  reliability: number; // 0-100
  lastSync: string;
  dataFreshness: string;
  coverageNotes: string;
}> = {
  EA: { ingestionStatus: 'automated', reliability: 95, lastSync: 'Today', dataFreshness: 'Live API', coverageNotes: 'Water discharges, waste ops, enforcement. OGL + EA Conditional licence.' },
  SoS: { ingestionStatus: 'semi-automated', reliability: 88, lastSync: 'Yesterday', dataFreshness: 'Daily polling', coverageNotes: 'PINS NSIP portal scraped daily. Covers DCO decisions and examination events.' },
  MMO: { ingestionStatus: 'manual-portal', reliability: 72, lastSync: '6 days ago', dataFreshness: 'Weekly', coverageNotes: 'Marine licensing portal. Document-led — structured extract not available.' },
  NELC: { ingestionStatus: 'semi-automated', reliability: 80, lastSync: '3 days ago', dataFreshness: 'Weekly', coverageNotes: 'Planning portal polling. Coverage limited to published discharge decisions.' },
  NELCPlanning: { ingestionStatus: 'semi-automated', reliability: 75, lastSync: '4 days ago', dataFreshness: 'Weekly', coverageNotes: 'Full planning portal including pre-commencement requirements.' },
  ABP: { ingestionStatus: 'manual', reliability: 45, lastSync: '12 days ago', dataFreshness: 'Manual', coverageNotes: 'No public register. Asset owner bilateral process only.' },
  AnglianWater: { ingestionStatus: 'manual-portal', reliability: 62, lastSync: '14 days ago', dataFreshness: 'Manual', coverageNotes: 'Developer services portal. No structured API. Case-by-case correspondence.' },
  NetworkRail: { ingestionStatus: 'manual-portal', reliability: 60, lastSync: '22 days ago', dataFreshness: 'Manual', coverageNotes: 'APE portal partially structured. Stage reviews are document-led.' },
  CadentGasLTD: { ingestionStatus: 'manual', reliability: 50, lastSync: '30 days ago', dataFreshness: 'Manual', coverageNotes: 'Developer services. No API. Method statement approval correspondence only.' },
  NaturalEngland: { ingestionStatus: 'manual', reliability: 55, lastSync: '18 days ago', dataFreshness: 'Manual', coverageNotes: 'Consultation responses only. No structured public register for HRA decisions.' },
};

const INGESTION_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  'automated': { label: 'Automated', color: '#16A34A', icon: <ConnectedIcon sx={{ fontSize: 14 }} /> },
  'semi-automated': { label: 'Semi-automated', color: '#2450C6', icon: <ConnectedIcon sx={{ fontSize: 14 }} /> },
  'manual-portal': { label: 'Portal (manual)', color: '#D97706', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  'manual': { label: 'Manual', color: '#94A3B8', icon: <ManualIcon sx={{ fontSize: 14 }} /> },
};

interface CGB {
  id: number;
  code: string;
  name: string;
  description: string | null;
  contact_email: string | null;
  website: string | null;
}

interface SourceMapEntry {
  source: string;
  access: string;
  update_frequency: string;
  difficulty: string;
  notes: string;
  source_url?: string;
}

interface ConsentType {
  id: number;
  code: string;
  name: string;
  default_cgb: { id: number; code: string; name: string } | null;
  source_map: SourceMapEntry | null;
}

interface ConsentSummary {
  id: number;
  cgb: { id: number; code: string; name: string } | null;
  created_at: string;
}

interface AuthorityConnector {
  connector_id: string;
  source_name: string;
  ingestion_mode: 'automated' | 'semi_automated' | 'manual';
  expected_cadence: string;
  active_record_count: number;
  last_successful_run: { finished_at: string | null } | null;
}

// Ingestion status derived from the dominant access method for an authority's consent types
const INGESTION_STATUS: Record<string, { label: string; color: string }> = {
  API: { label: 'Automated', color: '#16A34A' },
  Portal: { label: 'Semi-automated', color: '#2450C6' },
  'Portal/manual': { label: 'Portal (manual)', color: '#D97706' },
  Manual: { label: 'Manual', color: '#94A3B8' },
};

function mostFrequent(values: string[]): string | null {
  if (!values.length) return null;
  const counts: Record<string, number> = {};
  values.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

const ReliabilityBar: React.FC<{ score: number }> = ({ score }) => {
  const color = score >= 80 ? '#16A34A' : score >= 60 ? '#D97706' : '#94A3B8';
  return (
    <Tooltip title={`Data reliability score: ${score}/100`}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 90 }}>
        <Box sx={{ flex: 1, height: 5, bgcolor: '#F1F3F6', borderRadius: 0.5, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${score}%`, bgcolor: color }} />
        </Box>
        <Typography variant="caption" sx={{ color, fontWeight: 600, minWidth: 26 }}>{score}</Typography>
      </Stack>
    </Tooltip>
  );
};

const Authorities: React.FC = () => {
  const [cgbs, setCgbs] = useState<CGB[]>([]);
  const [consentTypes, setConsentTypes] = useState<ConsentType[]>([]);
  const [consents, setConsents] = useState<ConsentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CGB | null>(null);
  const [integrations, setIntegrations] = useState<Record<string, AuthorityConnector[]>>({});

  useEffect(() => {
    const load = async () => {
      try {
        const [cgbRes, typesRes, consentsRes, integrationsRes] = await Promise.all([
          fetch(`${API_BASE}/consents/cgbs`),
          fetch(`${API_BASE}/consents/consent-types`),
          fetch(`${API_BASE}/consents/?per_page=500`),
          fetch(`${API_BASE}/ingestion/authorities`),
        ]);
        setCgbs(await cgbRes.json());
        setConsentTypes(await typesRes.json());
        const consentsData = await consentsRes.json();
        setConsents(consentsData.consents || []);
        if (integrationsRes.ok) setIntegrations(await integrationsRes.json());
      } catch (err) {
        console.error('Failed to load authority intelligence data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const rows = useMemo(() => {
    return cgbs.map((cgb) => {
      const types = consentTypes.filter((ct) => ct.default_cgb?.id === cgb.id);
      const linkedConsents = consents.filter((c) => c.cgb?.id === cgb.id);
      const accessValues = types.map((t) => t.source_map?.access).filter(Boolean) as string[];
      const frequencyValues = types.map((t) => t.source_map?.update_frequency).filter(Boolean) as string[];
      const primaryAccess = mostFrequent(accessValues);
      const primaryFrequency = mostFrequent(frequencyValues);
      const lastActivity = linkedConsents
        .map((c) => c.created_at)
        .filter(Boolean)
        .sort()
        .reverse()[0];

      return {
        cgb,
        types,
        consentTypeCount: types.length,
        activeConsents: linkedConsents.length,
        primaryAccess,
        primaryFrequency,
        lastActivity,
      };
    });
  }, [cgbs, consentTypes, consents]);

  const statusTotals = useMemo(() => {
    const totals: Record<string, number> = { Automated: 0, 'Semi-automated': 0, 'Portal (manual)': 0, Manual: 0, Unmapped: 0 };
    rows.forEach((r) => {
      const status = r.primaryAccess ? INGESTION_STATUS[r.primaryAccess]?.label : null;
      totals[status || 'Unmapped'] += 1;
    });
    return totals;
  }, [rows]);

  if (loading) return <LinearProgress />;

  return (
    <Box>
      <PageHeader title="Regulatory Authorities" subtitle={<>The organisations whose decisions and records support your consent register. <TermHelp term="Data freshness" explanation="How recently a source was successfully checked for new or changed information." /></>} />

      {/* Status summary strip */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={{ xs: 1, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />} sx={{ width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
          {Object.entries(statusTotals).map(([label, count]) => (
            <Box key={label} sx={{ minWidth: 0, flex: { xs: '1 1 45%', sm: '0 1 auto' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h5" fontWeight={700}>{count}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Authority</TableCell>
              <TableCell>Consent Types</TableCell>
              <TableCell>Active Consents</TableCell>
              <TableCell>Ingestion</TableCell>
              <TableCell>Reliability</TableCell>
              <TableCell>Last Sync</TableCell>
              <TableCell>Update Cadence</TableCell>
              <TableCell align="right">Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ cgb, consentTypeCount, activeConsents, primaryAccess, primaryFrequency }) => {
              const configured = integrations[cgb.code] || [];
              const meta = AUTHORITY_METADATA[cgb.code];
              const configuredMode = configured[0]?.ingestion_mode;
              const ingestionKey = configuredMode === 'automated' ? 'automated'
                : configuredMode === 'semi_automated' ? 'semi-automated'
                : configuredMode === 'manual' ? 'manual-portal'
                : meta?.ingestionStatus || (primaryAccess ? (
                primaryAccess === 'API' ? 'automated' : primaryAccess === 'Portal' ? 'semi-automated' : primaryAccess === 'Portal/manual' ? 'manual-portal' : 'manual'
              ) : null);
              const ingMeta = ingestionKey ? INGESTION_META[ingestionKey] : null;
              const lastImport = configured.map((item) => item.last_successful_run?.finished_at).filter(Boolean).sort().reverse()[0];
              const connectorRecords = configured.reduce((total, item) => total + item.active_record_count, 0);
              return (
                <TableRow key={cgb.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(cgb)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{cgb.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{cgb.code}</Typography>
                  </TableCell>
                  <TableCell>{consentTypeCount}</TableCell>
                  <TableCell>
                    {(connectorRecords || activeConsents) > 0 ? (
                      <Chip size="small" label={connectorRecords || activeConsents} sx={{ bgcolor: '#EEF2FF', color: '#2450C6', fontWeight: 700 }} />
                    ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {ingMeta ? (
                      <Chip size="small" label={ingMeta.label} sx={{ bgcolor: ingMeta.color, color: '#fff' }} />
                    ) : (
                      <Chip size="small" label="Not mapped" sx={{ bgcolor: '#F1F3F6', color: 'text.secondary' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {meta ? <ReliabilityBar score={meta.reliability} /> : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lastImport ? new Date(lastImport).toLocaleString() : meta?.lastSync || '—'}</Typography>
                    {!!configured.length && <Typography variant="caption" color="text.secondary">{configured.length} connector{configured.length > 1 ? 's' : ''}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{configured[0]?.expected_cadence || meta?.dataFreshness || primaryFrequency || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {cgb.website && (
                      <MuiLink href={cgb.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <WebsiteIcon fontSize="small" />
                      </MuiLink>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Authority detail dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth PaperProps={{ sx: { width: '100%', maxWidth: { xs: 'calc(100vw - 24px)', sm: '100%' }, m: { xs: 1.5, sm: 3 } } }}>
        {selected && (
          <>
            <DialogTitle>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.55rem', sm: '2.125rem' } }}>{selected.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{selected.code}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              {selected.description && (
                <Typography variant="body2" sx={{ mb: 2 }}>{selected.description}</Typography>
              )}
              {AUTHORITY_METADATA[selected.code] && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#F9FAFB' }}>
                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">Data Reliability</Typography>
                        <ReliabilityBar score={AUTHORITY_METADATA[selected.code].reliability} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">Last Sync</Typography>
                        <Typography variant="body2" fontWeight={600}>{AUTHORITY_METADATA[selected.code].lastSync}</Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">Freshness</Typography>
                        <Typography variant="body2" fontWeight={600}>{AUTHORITY_METADATA[selected.code].dataFreshness}</Typography>
                      </Box>
                    </Stack>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Coverage Notes</Typography>
                      <Typography variant="body2">{AUTHORITY_METADATA[selected.code].coverageNotes}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
              <TableContainer sx={{ overflowX: 'auto', '& table': { minWidth: 560 } }}><Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Consent Type</TableCell>
                    <TableCell>Access</TableCell>
                    <TableCell>Difficulty</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.find((r) => r.cgb.id === selected.id)?.types.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{t.code}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.name}</Typography>
                      </TableCell>
                      <TableCell>{t.source_map?.access || '—'}</TableCell>
                      <TableCell>{t.source_map?.difficulty || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{t.source_map?.notes || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </DialogContent>
            <DialogActions>
              {selected.website && (
                <Button
                  href={selected.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  Visit Authority Site
                </Button>
              )}
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Authorities;
